import React, { useState } from "react";
import { Mail, Phone, Clock, ChevronDown, ChevronRight, MessageCircle } from "react-feather";
import logo from "../images/logo.png";

const SupportPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "1. How do I check the real-time availability of apartments/flats?",
      answer: "To check flat availability, log in to the app and navigate to the 'One Touch' dashboard on your home screen. Tap on the 'Availability' tab to view the total available units, broken down by specific apartment types (e.g., 2BHK, 3BHK, 4BHK) and their total saleable area."
    },
    {
      question: "2. Where can I see the total sales figures and booked flats?",
      answer: "You can view your overall sales performance right on the main dashboard. Look for the 'Total Sold' metric card. This section will give you a complete overview of all booked properties, including a comparison of pre-launch versus post-launch sales."
    },
    {
      question: "3. How do I find out how many flats are currently 'Blocked'?",
      answer: "On the One Touch dashboard, look for the 'Blocked' card under your inventory metrics. This number represents units that are temporarily held or blocked by management, separating them from the available and fully booked inventory."
    },
    {
      question: "4. Where can I access daily lead updates and collection reports?",
      answer: "Scroll down below the main metrics on your dashboard to find your specific report cards. By tapping on 'Lead Report', 'Daily Status', or 'Collection Report', you can view detailed, up-to-date information regarding customer leads and financial collections."
    },
    {
      question: "5. What should I do if my dashboard data is not updating or I cannot log in?",
      answer: "If you are experiencing login issues or your data is not refreshing, please ensure you have a stable internet connection and try restarting the app. If the problem persists, please contact your company's system administrator or reach out to us directly through the contact form on this support page."
    }
  ];

  const styles = {
    container: {
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
      fontFamily: "'Inter', sans-serif"
    },
    header: {
      backgroundColor: "#ffffff",
      padding: "1rem 2rem",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      display: "flex",
      alignItems: "center"
    },
    logo: {
      height: "40px",
      objectFit: "contain"
    },
    main: {
      maxWidth: "1000px",
      margin: "0 auto",
      padding: "2rem"
    },
    heroCard: {
      background: "linear-gradient(135deg, #2b5876 0%, #4e4376 100%)",
      borderRadius: "16px",
      padding: "3rem",
      color: "white",
      marginBottom: "2rem",
      boxShadow: "0 10px 20px rgba(0,0,0,0.1)"
    },
    badge: {
      background: "rgba(255,255,255,0.2)",
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "0.8rem",
      display: "inline-block",
      marginBottom: "1.5rem"
    },
    heroTitle: {
      fontSize: "2rem",
      fontWeight: "bold",
      marginBottom: "1rem"
    },
    heroDesc: {
      fontSize: "1rem",
      opacity: 0.9,
      maxWidth: "600px",
      lineHeight: 1.5
    },
    sectionTitle: {
      fontSize: "0.85rem",
      fontWeight: "bold",
      color: "#3f51b5",
      textTransform: "uppercase",
      letterSpacing: "1px",
      marginBottom: "1rem",
      marginTop: "2rem"
    },
    contactGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "1.5rem",
      marginBottom: "2rem"
    },
    contactCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "1.5rem",
      boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
      display: "flex",
      flexDirection: "column"
    },
    iconWrapper: {
      width: "40px",
      height: "40px",
      borderRadius: "8px",
      backgroundColor: "#f0f4f8",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "1rem",
      color: "#1F4E78"
    },
    contactLabel: {
      fontSize: "0.75rem",
      color: "#6c757d",
      textTransform: "uppercase",
      marginBottom: "0.5rem"
    },
    contactValue: {
      fontSize: "0.95rem",
      fontWeight: "600",
      color: "#212529"
    },
    faqContainer: {
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
      overflow: "hidden"
    },
    faqItem: {
      borderBottom: "1px solid #edf2f7",
      padding: "1.2rem 1.5rem",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column"
    },
    faqHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontWeight: "500",
      color: "#2d3748"
    },
    faqAnswer: {
      marginTop: "1rem",
      color: "#4a5568",
      fontSize: "0.95rem",
      lineHeight: 1.5
    },
    footerCard: {
      backgroundColor: "#eef2f6",
      borderRadius: "12px",
      padding: "2.5rem",
      textAlign: "center",
      marginTop: "3rem"
    },
    footerTitle: {
      fontSize: "1.2rem",
      fontWeight: "bold",
      color: "#1F4E78",
      marginBottom: "0.5rem"
    },
    footerDesc: {
      color: "#4a5568",
      fontSize: "0.95rem",
      marginBottom: "1.5rem"
    },
    button: {
      backgroundColor: "#3f51b5",
      color: "white",
      border: "none",
      padding: "10px 24px",
      borderRadius: "6px",
      fontWeight: "600",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "0.95rem",
      transition: "background-color 0.2s"
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <img src={logo} alt="BSS Logo" style={styles.logo} />
      </header>

      <main style={styles.main}>
        {/* Hero Section */}
        <div style={styles.heroCard}>
          <div style={styles.badge}>🌟 OFFICIAL SUPPORT</div>
          <h1 style={styles.heroTitle}>NEED HELP? WE'RE HERE FOR YOU!</h1>
          <p style={styles.heroDesc}>
            Get answers, report issues, or reach our team directly.<br/>
            BSS support is always just a message away.
          </p>
        </div>

        {/* Contact Info */}
        <div style={styles.sectionTitle}>CONTACT US</div>
        <div style={styles.contactGrid}>
          <div style={styles.contactCard}>
            <div style={styles.iconWrapper}><Mail size={20} /></div>
            <div style={styles.contactLabel}>EMAIL</div>
            <div style={styles.contactValue}>bss@bashyamgroup.com</div>
          </div>
          <div style={styles.contactCard}>
            <div style={styles.iconWrapper}><Phone size={20} /></div>
            <div style={styles.contactLabel}>PHONE</div>
            <div style={styles.contactValue}>+91 6385055698</div>
          </div>
          <div style={styles.contactCard}>
            <div style={styles.iconWrapper}><Clock size={20} /></div>
            <div style={styles.contactLabel}>OFFICE HOURS</div>
            <div style={styles.contactValue}>
              Mon-Fri<br/>
              9:00 AM - 6:00 PM IST
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div style={styles.sectionTitle}>FREQUENTLY ASKED QUESTIONS</div>
        <div style={styles.faqContainer}>
          {faqs.map((faq, index) => (
            <div key={index} style={styles.faqItem} onClick={() => toggleFaq(index)}>
              <div style={styles.faqHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ color: "#a0aec0", fontSize: "1.2rem" }}>📄</span>
                  {faq.question}
                </div>
                {openFaq === index ? <ChevronDown size={18} color="#a0aec0" /> : <ChevronRight size={18} color="#a0aec0" />}
              </div>
              {openFaq === index && (
                <div style={styles.faqAnswer}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Card */}
        <div style={styles.footerCard}>
          <div style={{ display: "inline-block", color: "#3f51b5", marginBottom: "1rem" }}>
            <MessageCircle size={32} />
          </div>
          <h2 style={styles.footerTitle}>STILL NEED HELP?</h2>
          <p style={styles.footerDesc}>
            Need more help? Contact our support team anytime. We're committed to helping you manage your workflow efficiently.
          </p>
          <a href="mailto:bss@bashyamgroup.com" style={{textDecoration: "none"}}>
            <button style={styles.button} onMouseOver={(e) => e.target.style.backgroundColor = "#303f9f"} onMouseOut={(e) => e.target.style.backgroundColor = "#3f51b5"}>
              <Mail size={18} />
              Contact Support
            </button>
          </a>
        </div>
      </main>
    </div>
  );
};

export default SupportPage;
